/**
 * Client for /api/v1/realtors/me/whatsapp, the realtor's WhatsApp Business
 * connection, and /api/v1/realtor-crm/whatsapp-review, the chats waiting to
 * be saved as contacts or ignored. Failures throw, for React Query's error
 * state.
 */

import type {
	LeadTemperature,
	LeadType,
} from "@/features/realtor/services/leads-api.service";
import type { ContactType } from "@/features/realtor/services/realtor-crm-api.service";
import { apiClient } from "@/lib/api-client";

export type WhatsappConnectionState = "connected" | "broken" | "disconnected";
export type WhatsappDisconnectReason =
	| "realtor_disconnected"
	| "partner_removed";
export type WhatsappSyncState =
	| "pending"
	| "syncing"
	| "complete"
	| "declined"
	| "failed";

export interface WhatsappConnection {
	status: WhatsappConnectionState;
	/** Set once the connection is disconnected. */
	disconnectReason: WhatsappDisconnectReason | null;
	/** As Meta formats it, e.g. "+91 98765 43210". */
	displayPhoneNumber: string;
	syncState: WhatsappSyncState;
	/** Meta's progress through the earlier-chats import, 0 to 100. */
	historyProgress: number;
	lastMessageAt: string | null;
	connectedAt: string;
	/** Review-list chats still waiting for a save or an ignore. */
	reviewEntriesLeft: number;
}

export interface WhatsappStatus {
	/** WhatsApp capture is switched on for this server. */
	available: boolean;
	/** Null when the realtor has never connected. */
	connection: WhatsappConnection | null;
}

export interface ConnectWhatsappInput {
	code: string;
	wabaId: string;
	/** Which wording of the client notice the realtor confirmed. */
	noticeVersion: number;
}

/** The `error` codes a refused connect answers with. */
export type WhatsappConnectErrorCode =
	| "CODE_EXPIRED"
	| "NUMBER_NOT_ELIGIBLE"
	| "NUMBER_IN_USE"
	| "WABA_NOT_GRANTED"
	| "META_UNAVAILABLE"
	| "WHATSAPP_CAPTURE_DISABLED";

const WHATSAPP_PATH = "/api/v1/realtors/me/whatsapp";

// Every caller handles these failures itself (the settings card and the review
// page with their own messages, Home and Contacts by showing nothing), so the
// global error toasts are skipped.
const INLINE = { inlineErrors: true } as const;

interface StatusEnvelope {
	data: { whatsapp: WhatsappStatus };
}

export async function getWhatsappStatus(): Promise<WhatsappStatus> {
	const response = await apiClient.get<StatusEnvelope>(WHATSAPP_PATH, INLINE);
	return response.data.data.whatsapp;
}

export async function connectWhatsapp(
	input: ConnectWhatsappInput,
): Promise<WhatsappStatus> {
	const response = await apiClient.post<StatusEnvelope>(
		`${WHATSAPP_PATH}/connect`,
		input,
		INLINE,
	);
	return response.data.data.whatsapp;
}

export async function disconnectWhatsapp(): Promise<WhatsappStatus> {
	const response = await apiClient.post<StatusEnvelope>(
		`${WHATSAPP_PATH}/disconnect`,
		undefined,
		INLINE,
	);
	return response.data.data.whatsapp;
}

/** A failed request's response body, when it is an object. */
function responseBody(error: unknown): Record<string, unknown> | undefined {
	const response = isRecord(error) ? error.response : undefined;
	const data = isRecord(response) ? response.data : undefined;
	return isRecord(data) ? data : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

/** The `error` code of a failed request, when the backend sent one. */
export function whatsappErrorCode(error: unknown): string | undefined {
	const code = responseBody(error)?.error;
	return typeof code === "string" ? code : undefined;
}

// Review list -------------------------------------------------------------------

/** The most chats one save or ignore takes; the backend refuses more. */
export const REVIEW_BATCH_MAX = 100;

export interface WhatsappReviewEntry {
	id: string;
	/**
	 * The name saved on the realtor's phone, else the sender's WhatsApp
	 * profile name, else the number.
	 */
	name: string;
	/** Whether `name` is the name saved on the realtor's phone. */
	hasSavedName: boolean;
	/** "+" and the number's digits. */
	number: string;
	/** Null when Finishd couldn't split the number: save needs a country code. */
	phoneCountryCode: string | null;
	phone: string | null;
	messageCount: number;
	newestMessageAt: string | null;
	/** When the chat arrived in the review list. */
	enteredAt: string;
	latestMessage: {
		direction: "inbound" | "outbound";
		kind: string;
		/** As recorded: media is a placeholder such as "[Photo] caption". */
		body: string;
		occurredAt: string;
	} | null;
}

export interface WhatsappReviewPage {
	entries: WhatsappReviewEntry[];
	/** Pass back as `cursor` for the next page; null on the last page. */
	nextCursor: string | null;
	/** Every chat the realtor has left to review. */
	total: number;
	/** Null when the realtor has never connected. */
	connection: {
		status: WhatsappConnectionState;
		syncState: WhatsappSyncState;
		historyProgress: number;
	} | null;
}

export interface WhatsappReviewSaveItem {
	entryId: string;
	contactType: ContactType;
	/** The new contact's name; the server falls back to the entry's name. */
	name?: string;
	/** Only for an entry whose `phoneCountryCode` is null. */
	countryCode?: string;
	/** Present when the realtor ticked the chat as an active deal. */
	lead?: { type: LeadType; temperature?: LeadTemperature };
}

export interface WhatsappReviewSaved {
	entryId: string;
	contactId: string;
	/** False when the number was already a contact, which took the chat. */
	createdContact: boolean;
	leadId: string | null;
}

/** The `error` codes a refused save or ignore answers with. */
export type WhatsappReviewErrorCode =
	| "REVIEW_ENTRIES_UNAVAILABLE"
	| "COUNTRY_CODE_REQUIRED"
	| "COUNTRY_CODE_INVALID";

const REVIEW_PATH = "/api/v1/realtor-crm/whatsapp-review";

/** One page of the review list, newest first. */
export async function listWhatsappReview(
	cursor?: string,
): Promise<WhatsappReviewPage> {
	const response = await apiClient.get<{ data: WhatsappReviewPage }>(
		REVIEW_PATH,
		{ ...INLINE, params: cursor ? { cursor } : {} },
	);
	return response.data.data;
}

export async function saveWhatsappReview(
	entries: WhatsappReviewSaveItem[],
): Promise<WhatsappReviewSaved[]> {
	const response = await apiClient.post<{
		data: { saved: WhatsappReviewSaved[] };
	}>(`${REVIEW_PATH}/save`, { entries }, INLINE);
	return response.data.data.saved;
}

export async function ignoreWhatsappReview(
	entryIds: string[],
): Promise<{ ignored: number }> {
	const response = await apiClient.post<{ data: { ignored: number } }>(
		`${REVIEW_PATH}/ignore`,
		{ entryIds },
		INLINE,
	);
	return response.data.data;
}

/** A refused save's or ignore's code, message and the entry ids it names. */
export function whatsappReviewFailure(error: unknown): {
	code: string | undefined;
	message: string | undefined;
	entryIds: string[];
} {
	const data = responseBody(error);
	const inner = data?.data;
	const ids = isRecord(inner) ? inner.entryIds : undefined;
	return {
		code: typeof data?.error === "string" ? data.error : undefined,
		message: typeof data?.message === "string" ? data.message : undefined,
		entryIds: Array.isArray(ids)
			? ids.filter((id): id is string => typeof id === "string")
			: [],
	};
}
